import React, { useEffect, useRef, useState } from "react";
import "../styles/kava.css";

/* Kava's display face is Space Grotesk; Aurora pages use Urbanist and must not
   pay for it. Injected on mount, removed on unmount. */
const KAVA_FONTS = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap";
function useKavaFonts() {
  React.useEffect(() => {
    if (document.querySelector(`link[href="${KAVA_FONTS}"]`)) return undefined;
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = KAVA_FONTS;
    document.head.appendChild(l);
    return () => { l.remove(); };
  }, []);
}

/**
 * KAVA — autonomous AI firewall landing page.
 *
 * Ported from the Lovable prototype (TanStack Router + Tailwind + TSX) into this
 * CRA app: Tailwind utilities became semantic classes in styles/kava.css, all of
 * which are scoped under .kava-root so the ember palette can't leak into Aurora.
 * Motion is preserved 1:1 and reduced-motion aware.
 *
 * NOTE: assets live in /public/kava-assets (NOT /public/kava) — a real `kava`
 * directory would satisfy nginx's `try_files $uri/` and shadow this SPA route
 * with a 403.
 */

const ASSET = (f) => `${process.env.PUBLIC_URL || ""}/kava-assets/${f}`;

const THREAT_FEED = [
  { k: "blk", t: "BLK · 192.34.11.7 · sql-injection" },
  { k: "blk", t: "BLK · 44.201.9.212 · l7-flood" },
  { k: "mit", t: "MIT · botnet-cluster · 12.4k rps" },
  { k: "iso", t: "ISO · lateral-move · vpc-east" },
  { k: "blk", t: "BLK · 178.62.44.9 · zero-day/CVE-2026-0417" },
  { k: "mit", t: "MIT · dns-tunnel · exfil-attempt" },
  { k: "blk", t: "BLK · 91.219.238.4 · credential-stuff" },
  { k: "iso", t: "ISO · container-escape · node-42" },
];

const STATS = [
  { num: 38, suffix: "ms", label: "Median neutralization" },
  { num: 9.4, suffix: "B", decimals: 1, label: "Requests inspected / day" },
  { num: 0, suffix: "", label: "Successful breaches" },
  { num: 199, suffix: "", label: "PoPs deployed globally" },
];

const MODULES = [
  { code: "01", name: "Sentinel Core", tag: "ml-runtime", desc: "Behavioral inference model retrained every 90 seconds on live edge telemetry." },
  { code: "02", name: "Kinetic Mesh", tag: "distribution", desc: "Anycast propagation of blocklists across 199 PoPs in under 400ms globally." },
  { code: "03", name: "Ghost Layer", tag: "deception", desc: "Decoy surface that fingerprints attackers before they touch production." },
  { code: "04", name: "Vault Rules", tag: "policy", desc: "Deterministic policy engine with cryptographically signed rollback." },
  { code: "05", name: "Signal Grid", tag: "observability", desc: "Realtime observability with sub-second query latency across petabytes." },
  { code: "06", name: "Zero Trust Bridge", tag: "ztna", desc: "Identity-bound access with continuous posture verification per request." },
];

const PLANS = [
  { id: "demo", name: "Free Demo", price: "₹0", period: "2 days", tagline: "Take a key. Test the perimeter.", cta: "Get demo key",
    features: ["Full access · 48 hours", "1 domain · 1 region", "Sentinel Core (evaluation)", "Community support"] },
  { id: "basic10", name: "Basic · 10 Year Key", price: "₹2,000", period: "one-time · 10 years", tagline: "Lifetime-grade static defense.", cta: "Buy 10-year key",
    features: ["Basic firewall ruleset", "1 domain · 3 regions", "Signed policy rollback", "Email support"] },
  { id: "monthly", name: "Operator · Monthly", price: "₹4,499", period: "per month", tagline: "Rolling protection for active teams.", cta: "Start monthly",
    features: ["All Basic features", "5 domains · 12 regions", "Kinetic Mesh propagation", "Priority chat support"] },
  { id: "yearly", name: "Command · 1 Year", price: "₹49,499", period: "per year", tagline: "Full arsenal. Autonomous ops.", cta: "Buy 1-year key", highlight: true,
    features: ["Everything in Operator", "Unlimited domains", "Ghost Layer + Signal Grid", "24×7 dedicated SOC line"] },
  { id: "triennial", name: "Fortress · 3 Year", price: "₹99,999", period: "3 years · locked rate", tagline: "Long-horizon sovereignty.", cta: "Buy 3-year key",
    features: ["Everything in Command", "Zero Trust Bridge included", "Dedicated threat analyst", "On-prem mesh option"] },
];

const COMPARISON = [
  { n: "Free Demo", t: "2 days", p: "₹0", pm: "—" },
  { n: "Basic · 10 Year", t: "10 years", p: "₹2,000", pm: "₹16.67" },
  { n: "Operator · Monthly", t: "1 month", p: "₹4,499", pm: "₹4,499" },
  { n: "Command · 1 Year", t: "12 months", p: "₹49,499", pm: "₹4,125" },
  { n: "Fortress · 3 Year", t: "36 months", p: "₹99,999", pm: "₹2,778" },
];

function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setSeen(true); io.disconnect(); }
    }, { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return { ref, seen };
}

function Reveal({ children, delay = 0, className = "", style }) {
  const { ref, seen } = useReveal();
  return (
    <div
      ref={ref}
      className={`${className} ${seen ? "k-reveal" : ""}`.trim()}
      style={{ ...style, opacity: seen ? undefined : 0, animationDelay: seen ? `${delay}ms` : undefined }}
    >
      {children}
    </div>
  );
}

function CountUp({ to, decimals = 0, suffix = "", duration = 1800 }) {
  const ref = useRef(null);
  const started = useRef(false);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const step = (now) => {
          const p = Math.min(1, (now - t0) / duration);
          setVal(to * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);
  return <span ref={ref}>{val.toFixed(decimals)}{suffix}</span>;
}

function Nav() {
  return (
    <header className="k-nav">
      <div className="k-nav-inner">
        <a href="#top" className="k-logo">
          <span className="k-hex"><i /><u /><b /></span>
          KAVA
          <span className="k-logo-sub">by getKhojo</span>
        </a>
        <nav className="k-nav-links">
          {["Arsenal", "Mesh", "Pricing", "Threat Lab"].map((l) => (
            <a key={l} className="k-nav-link" href={l === "Pricing" ? "#pricing" : "#arsenal"}>{l}</a>
          ))}
        </nav>
        <a href="#deploy" className="k-nav-cta">
          <span className="k-led"><span /><b /></span>
          Deploy
        </a>
      </div>
    </header>
  );
}

function Pricing() {
  return (
    <section id="pricing" className="k-pricing">
      <div className="k-pricing-grid-bg" aria-hidden="true" />
      <div className="k-section" style={{ padding: "0 24px" }}>
        <Reveal style={{ marginBottom: 56 }}>
          <div className="k-eyebrow"><span className="k-rule" /> 05 · Licensing</div>
          <h2 className="k-h2" style={{ marginTop: 16 }}>Choose your <span className="k-grad">key.</span></h2>
          <p className="k-mono" style={{ marginTop: 16, maxWidth: 640, fontSize: 13, color: "var(--k-muted)" }}>
            Powered by <span style={{ color: "var(--k-fg)" }}>getKhojo</span>. One license unlocks the full KAVA arsenal — pick the horizon that fits your operation.
          </p>
        </Reveal>

        <div className="k-plans">
          {PLANS.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <div className={`k-plan${p.highlight ? " hl" : ""}`}>
                {p.highlight && <span className="k-plan-badge">Recommended</span>}
                <div className="k-plan-name">{p.name}</div>
                <div className="k-plan-price">{p.price}</div>
                <div className="k-plan-period">{p.period}</div>
                <p className="k-plan-tag">{p.tagline}</p>
                <ul className="k-plan-feats">
                  {p.features.map((f) => (
                    <li key={f}><i>▸</i><span>{f}</span></li>
                  ))}
                </ul>
                <a href="#deploy" className="k-plan-cta"><span>{p.cta}</span><span>→</span></a>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal style={{ marginTop: 72 }}>
          <div className="k-eyebrow" style={{ color: "var(--k-muted)", marginBottom: 20 }}>
            <span className="k-rule" style={{ background: "var(--k-border)" }} /> Price comparison
          </div>
          <div className="k-table-wrap">
            <table className="k-table">
              <thead>
                <tr><th>Plan</th><th>Term</th><th>Price</th><th>Effective / month</th></tr>
              </thead>
              <tbody>
                {COMPARISON.map((r) => (
                  <tr key={r.n}>
                    <td className="n">{r.n}</td>
                    <td className="t">{r.t}</td>
                    <td className="n">{r.p}</td>
                    <td className="pm">{r.pm}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="k-mono" style={{ marginTop: 16, fontSize: 10, textTransform: "uppercase", letterSpacing: ".18em", color: "var(--k-muted)" }}>
            All prices in INR · Taxes billed separately · getKhojo licensing
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function KavaLanding() {
  useKavaFonts();
  const [mouse, setMouse] = useState({ x: 0.5, y: 0.5 });
  const loop = [...THREAT_FEED, ...THREAT_FEED];

  useEffect(() => {
    const prev = document.title;
    document.title = "KAVA — Autonomous AI Firewall | getKhojo";
    return () => { document.title = prev; };
  }, []);

  return (
    <div
      id="top"
      className="kava-root"
      onMouseMove={(e) => setMouse({ x: e.clientX / window.innerWidth, y: e.clientY / window.innerHeight })}
    >
      {/* ambient layers */}
      <div className="k-scanline" aria-hidden="true"><i /></div>
      <div className="k-noise" aria-hidden="true" />
      <div className="k-streams" aria-hidden="true">
        {[8, 22, 41, 58, 73, 88].map((left, i) => (
          <i key={i} style={{ left: `${left}%`, animationDelay: `${i * 0.9}s`, animationDuration: `${5 + (i % 3) * 1.5}s` }} />
        ))}
      </div>

      <Nav />

      {/* HERO */}
      <section className="k-hero">
        <div className="k-hero-bg">
          <img
            src={ASSET("hero.jpg")} alt="" width={1920} height={1200}
            style={{ transform: `scale(1.08) translate(${(mouse.x - 0.5) * -20}px, ${(mouse.y - 0.5) * -20}px)` }}
          />
          <div className="k-hero-fade" />
          <div className="k-hero-grid" />
        </div>

        <div className="k-embers" aria-hidden="true">
          <span className="a" /><span className="b" />
        </div>

        <div className="k-hero-inner">
          <div className="k-status">
            <span className="k-led"><span /><b /></span>
            <span className="k-flicker">System online · v9.14 · Sentinel active</span>
            <span className="k-caret" />
          </div>

          <h1 className="k-h1">
            The firewall<br />
            that <span className="k-grad k-glow">thinks back</span>.
          </h1>

          <p className="k-lead">
            KAVA is an autonomous, AI-driven perimeter. It anticipates threats before signatures exist,
            and neutralizes them in the space between two heartbeats.
          </p>

          <div className="k-cta-row">
            <a href="#deploy" className="k-btn"><span>Deploy KAVA</span><span className="k-mono">→</span><i /></a>
            <a href="#arsenal" className="k-btn-ghost">
              <span className="k-led"><span /><b /></span>
              Watch live defense
            </a>
          </div>
        </div>

        <div className="k-coords" aria-hidden="true">
          <div>
            <div>lat · 40.7128°N</div>
            <div>lng · 74.0060°W</div>
            <div className="k-flicker" style={{ color: "var(--k-primary)" }}>node · nyc-04-A</div>
          </div>
        </div>
      </section>

      {/* THREAT TICKER */}
      <section className="k-ticker">
        <div className="k-ticker-track">
          {loop.map((t, i) => (
            <span key={i} className="k-ticker-item"><i className={t.k} />{t.t}</span>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="k-section">
        <div className="k-stats">
          {STATS.map((s, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="k-stat">
                <div className="k-stat-num k-grad">
                  <CountUp to={s.num} decimals={s.decimals || 0} suffix={s.suffix} />
                </div>
                <div className="k-stat-label">{s.label}</div>
                <div className="k-stat-idx">0{i + 1}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="arsenal" className="k-bordered">
        <div className="k-split">
          <Reveal className="k-split-media">
            <img src={ASSET("shield.jpg")} alt="" loading="lazy" width={1400} height={1000} />
            <div className="fade" />
            <div className="k-rings" aria-hidden="true">
              <i /><i style={{ animationDelay: "0.9s" }} />
            </div>
          </Reveal>
          <Reveal delay={150} className="k-split-body">
            <div className="k-eyebrow"><span className="k-rule" /> Manifesto · 01</div>
            <h2 className="k-h2" style={{ marginTop: 24 }}>
              Legacy firewalls<br />
              <span className="k-strike">react</span>.<br />
              KAVA <span className="k-grad">predicts</span>.
            </h2>
            <p style={{ marginTop: 32, maxWidth: 520, fontSize: 18, lineHeight: 1.7, color: "var(--k-muted)" }}>
              Every packet is scored against a 4.2 billion-parameter behavioral model — trained not on last
              year's CVEs, but on live adversarial traffic hitting our mesh right now.
            </p>
            <dl className="k-dl">
              <div><dt>Inference</dt><dd>edge-local</dd></div>
              <div><dt>Model refresh</dt><dd>90 seconds</dd></div>
            </dl>
          </Reveal>
        </div>
      </section>

      {/* ARSENAL */}
      <section className="k-bordered">
        <div className="k-section">
          <div className="k-arsenal-head">
            <Reveal>
              <div className="k-eyebrow"><span className="k-rule" /> Arsenal · 06 modules</div>
              <h2 className="k-h2" style={{ marginTop: 16 }}>One perimeter.<br />Six calibrated weapons.</h2>
            </Reveal>
          </div>
          <div className="k-modules">
            {MODULES.map((m, i) => (
              <Reveal key={m.code} delay={i * 90}>
                <div className="k-module">
                  <div className="k-mod-top">
                    <span className="k-mod-code">{m.code}</span>
                    <span className="k-mod-tag">/{m.tag}</span>
                  </div>
                  <h3 className="k-mod-name">{m.name}</h3>
                  <p className="k-mod-desc">{m.desc}</p>
                  <div className="k-mod-cta">Engage →</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MESH */}
      <section className="k-mesh">
        <img src={ASSET("network.jpg")} alt="Global mesh" loading="lazy" width={1400} height={900} />
        <div className="k-mesh-fade" />
        <div aria-hidden="true">
          {[{ top: "22%", left: "62%" }, { top: "48%", left: "78%" }, { top: "68%", left: "58%" }, { top: "35%", left: "88%" }].map((p, i) => (
            <span key={i} className="k-mesh-node" style={{ top: p.top, left: p.left }}>
              <i style={{ animationDelay: `${i * 0.5}s` }} /><b />
            </span>
          ))}
        </div>
        <div className="k-mesh-body">
          <Reveal style={{ maxWidth: 620 }}>
            <div className="k-eyebrow">Mesh · realtime</div>
            <h2 className="k-h2" style={{ marginTop: 16 }}>Your traffic never<br />leaves the <span className="k-grad">edge</span>.</h2>
            <p style={{ marginTop: 24, fontSize: 18, color: "var(--k-muted)" }}>
              Inspection, decisioning, and mitigation happen at the PoP nearest to your user.
              No hairpinning. No cloud detour. No compromise.
            </p>
          </Reveal>
        </div>
        <div className="k-mesh-meta">
          <div className="k-flicker" style={{ color: "var(--k-primary)" }}>199 nodes online</div>
          <div>uptime · 99.9997%</div>
        </div>
      </section>

      {/* DEPLOY */}
      <section id="deploy" className="k-deploy">
        <div className="k-deploy-glow" aria-hidden="true" />
        <div className="k-deploy-inner">
          <Reveal>
            <div className="k-eyebrow" style={{ justifyContent: "center" }}>
              <span className="k-rule" /> Initiate · 04 <span className="k-rule" />
            </div>
            <h2 className="k-h2" style={{ marginTop: 24, fontSize: "clamp(2.6rem,7vw,5rem)" }}>
              Deploy in<br /><span className="k-grad k-glow">4 minutes</span>.
            </h2>
            <p style={{ margin: "32px auto 0", maxWidth: 560, fontSize: 18, color: "var(--k-muted)" }}>
              One DNS record. One API key. Full mesh protection with instant rollback if anything looks off.
            </p>
            <div className="k-cta-row" style={{ justifyContent: "center", marginTop: 48 }}>
              <a href="#pricing" className="k-btn"><span>Request early access</span><span className="k-mono">→</span><i /></a>
              <a href="#pricing" className="k-mono" style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".18em", color: "var(--k-muted)" }}>
                or compare licences ↗
              </a>
            </div>
            <div className="k-badges">
              <div>SOC 2 Type II</div><div>ISO 27001</div><div>FedRAMP High</div>
            </div>
          </Reveal>
        </div>
      </section>

      <Pricing />

      <footer className="k-footer">
        <div className="k-footer-inner">
          <div>© {new Date().getFullYear()} KAVA · by getKhojo · Autonomous perimeter systems</div>
          <div className="k-footer-links">
            <a href="#top" className="k-flicker">Status · operational</a>
            <a href="/security">Security</a>
            <a href="/privacy">Privacy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
